import type { ReactNode } from "react";

/**
 * The portal's icon set, inline.
 *
 * Inline SVG rather than an icon package: the portal has no icon dependency and
 * adding one to draw nine glyphs would be a whole package in the lockfile, a
 * whole licence to check and a whole thing to keep upgraded, for nine paths.
 *
 * Every glyph is drawn on a 16-unit grid with a 1.6 stroke so they sit at the
 * same optical weight as the 14px nav label beside them, and every one is
 * `aria-hidden`: the label next to it already names the destination, and a
 * screen reader announcing "layers, Pipeline" is worse than silence.
 */

function Glyph({
  children,
  size = 16,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Pipeline — stacked stages. */
export function IconPipeline(): ReactNode {
  return (
    <Glyph>
      <path d="M2.5 4.2 8 1.7l5.5 2.5L8 6.7 2.5 4.2Z" />
      <path d="M2.5 8 8 10.5 13.5 8" />
      <path d="M2.5 11.6 8 14.1l5.5-2.5" />
    </Glyph>
  );
}

/** Tasks — a checked list. */
export function IconTasks(): ReactNode {
  return (
    <Glyph>
      <path d="M2.2 4.4 3.5 5.7l2.2-2.4" />
      <path d="M2.2 11 3.5 12.3l2.2-2.4" />
      <path d="M8.4 4.6h5.4M8.4 11.2h5.4" />
    </Glyph>
  );
}

/** Deadlines — a clock. */
export function IconClock(): ReactNode {
  return (
    <Glyph>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.6V8l2.4 1.6" />
    </Glyph>
  );
}

/** Jobs map — a map pin. */
export function IconMapPin(): ReactNode {
  return (
    <Glyph>
      <path d="M8 14.4s-4.6-4.3-4.6-7.7a4.6 4.6 0 0 1 9.2 0c0 3.4-4.6 7.7-4.6 7.7z" />
      <circle cx="8" cy="6.6" r="1.7" />
    </Glyph>
  );
}

/** Installations — a camera, because what arrives from the field is photographs. */
export function IconInstallations(): ReactNode {
  return (
    <Glyph>
      <path d="M1.8 5.4h2.6L5.6 3.4h4.8l1.2 2h2.6v8H1.8z" />
      <circle cx="8" cy="9.1" r="2.4" />
    </Glyph>
  );
}

/** Customers — two people. */
export function IconCustomers(): ReactNode {
  return (
    <Glyph>
      <circle cx="6.2" cy="5.4" r="2.5" />
      <path d="M1.8 13.6c0-2.3 2-3.8 4.4-3.8s4.4 1.5 4.4 3.8" />
      <path d="M11 3.2a2.5 2.5 0 0 1 0 4.6M12.4 10.3c1.2.5 1.9 1.6 1.9 3.1" />
    </Glyph>
  );
}

/** Retrofit — a dwelling. */
export function IconRetrofit(): ReactNode {
  return (
    <Glyph>
      <path d="M2.2 6.9 8 2.2l5.8 4.7v6.3a.8.8 0 0 1-.8.8H3a.8.8 0 0 1-.8-.8V6.9Z" />
      <path d="M6.3 14V9.2h3.4V14" />
    </Glyph>
  );
}

/** Messages — a conversation. */
export function IconMessages(): ReactNode {
  return (
    <Glyph>
      <path d="M14 9.3a1.6 1.6 0 0 1-1.6 1.6H5.2L2 13.7V3.5a1.6 1.6 0 0 1 1.6-1.6h8.8A1.6 1.6 0 0 1 14 3.5v5.8Z" />
    </Glyph>
  );
}

/** Notifications — a bell. */
export function IconBell(): ReactNode {
  return (
    <Glyph>
      <path d="M12.2 6.6a4.2 4.2 0 1 0-8.4 0c0 4.2-1.6 5.4-1.6 5.4h11.6s-1.6-1.2-1.6-5.4Z" />
      <path d="M6.9 14.1a1.3 1.3 0 0 0 2.2 0" />
    </Glyph>
  );
}

/** Team — people with a mark of authority. */
export function IconTeam(): ReactNode {
  return (
    <Glyph>
      <circle cx="8" cy="5" r="2.6" />
      <path d="M2.8 13.8c0-2.6 2.3-4.3 5.2-4.3s5.2 1.7 5.2 4.3" />
    </Glyph>
  );
}

/** The company — a building with a door, beside the people in it. */
export function IconCompany(): ReactNode {
  return (
    <Glyph>
      <path d="M3.2 14.2V4.4l5.4-1.8v11.6" />
      <path d="M8.6 7.2h4.2v7h-9.6" />
      <path d="M5.6 6.4v.01M5.6 9v.01M5.6 11.6v.01M10.6 10v.01" />
    </Glyph>
  );
}

/** Help — a question mark in a ring. */
export function IconHelp(): ReactNode {
  return (
    <Glyph>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M6.2 6.3a1.9 1.9 0 0 1 3.7.5c0 1.3-1.9 1.6-1.9 2.9" />
      <path d="M8 11.6v.1" />
    </Glyph>
  );
}

/** New lead — a plus. */
export function IconPlus(): ReactNode {
  return (
    <Glyph>
      <path d="M8 3.2v9.6M3.2 8h9.6" />
    </Glyph>
  );
}

/**
 * The Scannix mark: a scan sweep across an enclosed volume. Drawn white on the
 * accent tile in the rail, so it is a single-weight stroke with no fills.
 */
export function IconScannix(): ReactNode {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.4 5.6V3.4a1 1 0 0 1 1-1h2.2M12.4 2.4h2.2a1 1 0 0 1 1 1v2.2M15.6 12.4v2.2a1 1 0 0 1-1 1h-2.2M5.6 15.6H3.4a1 1 0 0 1-1-1v-2.2" />
      <path d="M5 9h8" />
      <path d="M6.6 6.4h4.8M6.6 11.6h4.8" opacity="0.55" />
    </svg>
  );
}

/** The glyph inside a designed loading / empty / failure block. */
export function IconState({
  kind,
}: {
  readonly kind: "empty" | "refused" | "failed" | "offline";
}): ReactNode {
  if (kind === "failed") {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="8" cy="8" r="6.3" />
        <path d="M8 4.8v3.8M8 11.1h.01" />
      </svg>
    );
  }
  if (kind === "refused") {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        aria-hidden="true"
        focusable="false"
      >
        <rect x="3.4" y="7.1" width="9.2" height="6.5" rx="1.2" />
        <path d="M5.6 7.1V5.3a2.4 2.4 0 0 1 4.8 0v1.8" />
      </svg>
    );
  }
  if (kind === "offline") {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M2 5.6a9 9 0 0 1 12 0M4.5 8.4a5.5 5.5 0 0 1 7 0M8 12.4h.01" />
        <path d="M2.6 2.4l10.8 11.2" />
      </svg>
    );
  }
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.2" y="3.4" width="11.6" height="9.2" rx="1.4" />
      <path d="M2.2 6.6h11.6" />
    </svg>
  );
}
